/**
 * The instructor APIs: enrolments, grades, per-learner progress, certificates.
 *
 * ALL OF THIS IS THE PLATFORM'S OWN REST, and finding that out was the whole job. The obvious
 * route — embedding the server-rendered instructor dashboard in a frame — was tried and thrown
 * away: it is iframes, it busts out of them by default, and it is deprecated upstream anyway
 * (DEPR-38432). Underneath it sits a modern DRF surface that owes nothing to that page:
 *
 *   /api/instructor/v2/courses/{id}/enrollments            list, paginated
 *   /api/instructor/v2/courses/{id}/enrollments/modify     enrol and unenrol in bulk
 *   /api/instructor/v2/courses/{id}/beta_testers/modify    same, for beta access
 *   /api/instructor/v2/courses/{id}/certificates/…         issued, config, exceptions,
 *                                                          generation_history, regenerate
 *   /api/pathways/v1/courses/{id}/reports                  OURS. The report exports are the
 *   /api/pathways/v1/courses/{id}/reports/{type}/generate  one surface here that does NOT
 *   /api/pathways/v1/courses/{id}/reports/{name}/download  use the platform's endpoints --
 *                                                          see the reports section below for
 *                                                          the three measured reasons it
 *                                                          cannot.
 *   /api/instructor/v2/courses/{id}/instructor_tasks       what is running now
 *   /api/grades/v1/gradebook/{id}/                         every learner's grade, with a
 *                                                          per-assignment breakdown
 *   /api/grades/v1/gradebook/{id}/grading-info             cutoffs and assignment types
 *   /api/course_home/v1/progress/{id}/{user_id}/           one learner's progress
 *
 * Every shape below was read off the running server, not from documentation — the comments say
 * where a field surprised us.
 *
 * TENANCY, AND ONE PLACE THAT CLAIM DOES NOT HOLD. These are course-scoped, and a course id
 * reaches them straight from the picker, so the tenant check happens where the picker gets its
 * list (`listCourses` is site-scoped) and again in the platform.
 *
 * The second half of that is weaker than it sounds and it is worth writing down: the
 * platform's check is `HasAccessRule('staff')`, which any `is_staff` user satisfies for EVERY
 * course on the deployment, and every thinlms tenant admin is `is_staff=true`. For most of
 * these endpoints that is a pre-existing property of the course path. For REPORTS it is not
 * acceptable, because a grade report is the whole roster with email addresses -- so that one
 * surface goes through our own tenant-scoped API instead. See the reports section below.
 */

import { openedx, request } from './openedx';

const course = (courseId: string) => encodeURIComponent(courseId);
const V2 = (courseId: string) => `/api/instructor/v2/courses/${course(courseId)}`;

/** edx-platform's DefaultPagination, which several of these share. */
export interface Paged<T> {
  results: T[];
  count?: number;
  num_pages?: number;
  current_page?: number;
  next: string | null;
  previous: string | null;
}

/* --------------------------------------------------------------- enrolments ---- */

/**
 * One enrolment. NOTE what is absent: there is no enrolment DATE and no active flag — the
 * endpoint lists current enrolments only, so a page cannot show "enrolled since" from this.
 */
export interface Enrolment {
  username: string;
  full_name: string;
  email: string;
  /** 'honor' | 'audit' | 'verified' … the mode decides certificate eligibility. */
  mode: string;
  is_beta_tester: boolean;
}

export const listEnrolments = (courseId: string, page = 1) =>
  openedx.get<Paged<Enrolment>>(`${V2(courseId)}/enrollments?page=${page}&page_size=100`);

/**
 * Enrol or unenrol people, by email or username, in one call.
 *
 * `auto_enrol` decides what happens to an address with no account yet: with it on the platform
 * records an invitation that takes effect when they register, and with it off the address is
 * skipped. `notify` sends the platform's own email.
 *
 * The response reports per identifier, because a batch of fifty can fail for one of them and
 * saying "done" would hide that.
 */
export interface EnrolmentChange {
  identifiers: string[];
  action: 'enroll' | 'unenroll';
  auto_enrol: boolean;
  notify: boolean;
}

/** The state of one person's enrolment, before or after the change. */
export interface EnrolmentState {
  /** Whether an account exists for this identifier at all. */
  user: boolean;
  enrollment: boolean;
  /** An INVITATION: recorded for an address with no account, applied when they register. */
  allowed: boolean;
  auto_enroll: boolean;
}

/**
 * One identifier's result. The endpoint answers per person, never as a single verdict.
 *
 * THE VIEW'S DOCSTRING IS WRONG ABOUT THIS, and it cost a debugging round. It documents
 * `{identifier, success, user_is_registered, enrollment, allowed, auto_enroll}`; the code
 * actually returns `{identifier, before: {…}, after: {…}}` with no `success` anywhere. Measured
 * against the running server. Reading the documented field reported every enrolment as refused
 * while the platform was cheerfully enrolling people — the worst kind of wrong, because the
 * screen contradicted itself.
 *
 * Both shapes are accepted below, in case a later release delivers what its docstring promises.
 */
export interface EnrolmentOutcome {
  identifier: string;
  before?: EnrolmentState;
  after?: EnrolmentState;
  /** Present only on a refusal, and carries the reason. */
  error?: string | boolean;
  /** Documented but not currently sent. */
  success?: boolean;
}

/**
 * Did this one identifier end up in the state we asked for?
 *
 * Derived from `after` rather than assumed from a 200: the request succeeds as a whole while
 * individual addresses are refused, which is exactly the case a single verdict would hide.
 *
 * An enrol counts as done when the person is enrolled OR has an invitation waiting — the second
 * is what happens for an address with no account yet, and it is a success, not a failure.
 */
export function outcomeSucceeded(outcome: EnrolmentOutcome, action: 'enroll' | 'unenroll'): boolean {
  if (outcome.error) return false;
  if (typeof outcome.success === 'boolean') return outcome.success;
  if (!outcome.after) return false;
  return action === 'enroll'
    ? outcome.after.enrollment || outcome.after.allowed
    : !outcome.after.enrollment;
}

/** Why an identifier was refused, in the platform's words where it gave any. */
export function outcomeReason(outcome: EnrolmentOutcome): string {
  if (typeof outcome.error === 'string' && outcome.error) return outcome.error;
  if (outcome.after && !outcome.after.user) {
    return 'no account with that address, and inviting addresses without an account was switched off';
  }
  return 'the academy refused it without saying why';
}

/**
 * `identifier`, SINGULAR, and a LIST.
 *
 * Worth stating because the plural reads more natural and is silently wrong: sending
 * `identifiers` — or sending one comma-separated string, which is what the old dashboard's form
 * posted — is refused outright, and the refusal arrives as a flat 400 that names no field. Read
 * off the view's own docstring in `instructor/views/api_v2.py`.
 */
export const modifyEnrolments = (courseId: string, change: EnrolmentChange) =>
  openedx.post<{ action: string; results: EnrolmentOutcome[] }>(`${V2(courseId)}/enrollments/modify`, {
    identifier: change.identifiers,
    action: change.action,
    auto_enroll: change.auto_enrol,
    email_students: change.notify,
  });

/** Beta access. Same shape, except the actions are 'add' and 'remove' rather than enrol/unenrol. */
export const modifyBetaTesters = (courseId: string, change: EnrolmentChange) =>
  openedx.post<{ action: string; results: EnrolmentOutcome[] }>(`${V2(courseId)}/beta_testers/modify`, {
    identifier: change.identifiers,
    action: change.action === 'enroll' ? 'add' : 'remove',
    email_students: change.notify,
    auto_enroll: change.auto_enrol,
  });

/* -------------------------------------------------------------------- grades ---- */

/** One assignment's score for one learner, from the gradebook's `section_breakdown`. */
export interface SubsectionScore {
  attempted: boolean;
  /** The assignment TYPE, e.g. 'Quiz' — the grading policy's name for it. */
  category: string;
  /** Short label the gradebook column uses, e.g. 'Quiz 01'. */
  label: string;
  module_id: string;
  percent: number;
  score_earned: number;
  score_possible: number;
  subsection_name: string;
}

export interface GradebookRow {
  user_id: number;
  username: string;
  /** Frequently EMPTY on this endpoint even when the account has one — do not rely on it. */
  email: string;
  /** 0–1, not a percentage. */
  percent: number;
  section_breakdown: SubsectionScore[];
}

/**
 * The gradebook. Its pagination is NOT the usual shape: no `count`, no `num_pages` — it reports
 * `total_users_count` and `filtered_users_count` and gives cursor links instead.
 */
export interface Gradebook {
  results: GradebookRow[];
  total_users_count: number;
  filtered_users_count: number;
  next: string | null;
  previous: string | null;
}

export function listGradebook(courseId: string, params: { search?: string; pageSize?: number } = {}) {
  const query = new URLSearchParams({ page_size: String(params.pageSize ?? 50) });
  if (params.search) query.set('user_contains', params.search);
  return openedx.get<Gradebook>(`/api/grades/v1/gradebook/${course(courseId)}/?${query}`);
}

export interface GradingInfo {
  /** Letter grade thresholds as fractions, e.g. `{ Pass: 0.5 }`. */
  grade_cutoffs: Record<string, number>;
  assignment_types: Record<
    string,
    { type: string; weight: number; min_count: number; drop_count: number; short_label?: string }
  >;
  subsections: { module_id: string; display_name: string; assignment_type: string }[];
  grades_frozen: boolean;
  can_see_bulk_management: boolean;
}

export const getGradingInfo = (courseId: string) =>
  openedx.get<GradingInfo>(`/api/grades/v1/gradebook/${course(courseId)}/grading-info`);

/* ------------------------------------------------------------------ progress ---- */

/**
 * One learner's progress — the same payload the platform's own progress page renders from.
 *
 * KEYED BY NUMERIC USER ID, not username: `/…/progress/{course}/{user_id}/`, with the trailing
 * slash. A username there is a 404, and so is a missing slash.
 *
 * It is also gated by the `course_home.course_home_mfe_progress_tab` waffle flag, which the
 * Tutor plugin turns on — `ProgressTabView.get` raises Http404 when it is off, so the endpoint
 * looks like a wrong URL rather than a disabled feature.
 */
export interface LearnerProgress {
  completion_summary: { complete_count: number; incomplete_count: number; locked_count: number };
  course_grade: { letter_grade: string | null; percent: number; is_passing: boolean };
  section_scores: {
    display_name: string;
    subsections: {
      display_name: string;
      /** Present only for graded subsections. */
      assignment_type?: string | null;
      has_graded_assignment: boolean;
      num_points_earned: number;
      num_points_possible: number;
      percent_graded: number;
      learner_has_access: boolean;
      show_grades: boolean;
      url?: string;
    }[];
  }[];
  certificate_data?: { cert_status?: string; cert_web_view_url?: string | null } | null;
  grading_policy?: {
    assignment_policies: { type: string; weight: number; num_droppable: number; num_total: number }[];
    grade_range: Record<string, number>;
  };
  enrollment_mode?: string;
  has_scheduled_content?: boolean;
}

export const getLearnerProgress = (courseId: string, userId: number) =>
  openedx.get<LearnerProgress>(`/api/course_home/v1/progress/${course(courseId)}/${userId}/`);

/* -------------------------------------------------------------- certificates ---- */

/** Whether this course issues certificates at all. `{ enabled: boolean }`. */
export const getCertificateConfig = (courseId: string) =>
  openedx.get<{ enabled: boolean }>(`${V2(courseId)}/certificates/config`);

export const toggleCertificateGeneration = (courseId: string, enabled: boolean) =>
  openedx.post<{ enabled?: boolean; message?: string }>(`${V2(courseId)}/certificates/toggle_generation`, {
    certificates_enabled: enabled,
  });

/** One entry in the generation history — each is an instructor task, not a certificate. */
export interface GenerationHistoryEntry {
  task_name?: string;
  created?: string;
  task_state?: string;
  requested_by?: string;
  regenerated?: boolean;
  [extra: string]: unknown;
}

export const listGenerationHistory = (courseId: string) =>
  openedx.get<GenerationHistoryEntry[] | Paged<GenerationHistoryEntry>>(
    `${V2(courseId)}/certificates/generation_history`,
  );

/**
 * Regenerate certificates for learners in the given statuses.
 *
 * Statuses are the platform's, and picking none is not "all" — it is a no-op, which is why the
 * caller has to choose.
 */
export const regenerateCertificates = (courseId: string, statuses: string[]) =>
  openedx.post<{ message?: string; success?: boolean }>(`${V2(courseId)}/certificates/regenerate`, {
    certificate_statuses: statuses,
  });

/* --------------------------------------------------------------------- reports ---- */

/**
 * REPORTS DO NOT USE THE PLATFORM'S ENDPOINTS. Everything else in this file does; this
 * section deliberately does not, and the reason is the tenant note at the top of the file
 * being wrong for exactly this surface.
 *
 * `/api/instructor/v2/courses/{id}/reports` and `.../reports/{type}/generate` gate on
 * `InstructorPermission`, which resolves to "any `is_staff` user, for every course on the
 * deployment". Every thinlms tenant admin is `is_staff=true`. A grade report is the roster —
 * names, usernames, email addresses and every score — so those endpoints would let one
 * academy's administrator export another academy's learners.
 *
 * They also could not be made to work as they stand:
 *
 *   * the identifiers were wrong. This file used to say the two values that matter are
 *     `grade_report` and `problem_grade_report`. They are `grade` and `problem_grade`
 *     (`ReportType` in `lms/djangoapps/instructor/constants.py`), and `report_handlers.get()`
 *     does no normalisation, so both spellings returned HTTP 400 and no report was ever
 *     generated on this deployment;
 *   * the response shape was wrong too. `ReportFile` here declared `name` and `url`;
 *     `ReportDownloadsView` returns `report_name`, `report_url`, `date_generated` and
 *     `report_type`. Even with files present, every link would have rendered with no text
 *     and `href="undefined"`;
 *   * `report_url` is `/media/grades/<sha1>/…`, which 404s under `DEBUG=False` — and which
 *     must NEVER be made to work, because that path is authenticated by nothing.
 *
 * So these three call `/api/pathways/v1/…`, our own tenant-scoped door. See
 * `openedx_thinlms_pathways/report_views.py`.
 */

const REPORTS = (courseId: string) => `/api/pathways/v1/courses/${course(courseId)}/reports`;

/**
 * A report this academy may ask for.
 *
 * THE SPA HOLDS NO REPORT IDENTIFIER OF ITS OWN. These come from the server, which reads them
 * off the platform's own `ReportType` enum. That is what makes the drift that broke this
 * surface — a literal in TypeScript against a StrEnum in Python — unrepeatable: there is no
 * second copy left to drift.
 */
export interface AvailableReport {
  report_type: string;
  label: string;
  description: string;
}

/** A generated report file, as our own endpoint describes it. */
export interface ReportFile {
  name: string;
  report_type: string;
  size_bytes: number;
  /** ISO-8601 UTC, or null when the store could not say — never a fabricated timestamp. */
  generated_at: string | null;
}

/** One report job. `state` is the platform's own `InstructorTask.task_state`. */
export interface ReportTask {
  task_id: string;
  report_type: string;
  state: string;
  running: boolean;
  succeeded: boolean;
  failed: boolean;
  requested_by: string | null;
  requested_at: string | null;
  detail: string | null;
}

/**
 * Whether the store could be read at all.
 *
 * `"unreadable"` is a FAULT and not an empty state: it means either that the store raised, or
 * that a report job finished successfully and none of the files it wrote can be seen from the
 * process that answered. The second is the shape this whole change exists to remove — the
 * platform's own `links_for` returns `[]` for it, and a screen above that says "no reports
 * yet", which is a lie an administrator cannot act on.
 *
 * The `status` is typed as a plain string on purpose. A union of the two known values would
 * make an unrecognised third value a TYPE error rather than a runtime state, and the UI's rule
 * is `status !== 'ok'` is a fault — including when the field is missing entirely.
 */
export interface ReportStoreHealth {
  status: string;
  detail: string | null;
}

export interface ReportsPayload {
  api_version?: string;
  course_id?: string;
  available?: AvailableReport[];
  store?: ReportStoreHealth;
  results?: ReportFile[];
  tasks?: ReportTask[];
  generating?: ReportTask[];
  failed?: ReportTask[];
}

export const listReports = (courseId: string) =>
  openedx.get<ReportsPayload>(REPORTS(courseId));

/**
 * Ask for a report. It runs as a background job: the 202 means a row was reserved and a task
 * published, NOT that a file exists. `task_id` identifies that job in the `tasks` list on the
 * next poll, so the screen can say "yours is running" rather than showing the same list it
 * showed before the click.
 *
 * `reportType` must be a `report_type` the server offered in `available`. Nothing here
 * invents one.
 */
export const generateReport = (courseId: string, reportType: string) =>
  request<{
    status?: string;
    report_type?: string;
    task_id?: string | null;
    state?: string;
  }>(`${REPORTS(courseId)}/${encodeURIComponent(reportType)}/generate`, {
    method: 'POST',
    json: {},
  });

/**
 * Where a report file is fetched from.
 *
 * AUTHENTICATED AND TENANT-SCOPED, which is why it is a URL on our own API and not the
 * `report_url` the platform hands out. Followed as a link rather than fetched, because the
 * response is a `Content-Disposition: attachment` stream and the browser should save it; the
 * session cookie goes with it exactly as it does with any other request to this origin.
 */
export const reportDownloadUrl = (courseId: string, name: string) =>
  `${REPORTS(courseId)}/${encodeURIComponent(name)}/download`;

export interface InstructorTask {
  task_type: string;
  task_input: string;
  task_id: string;
  requester: string;
  task_state: string;
  created: string;
  status?: string;
  duration_sec?: number | string;
  task_message?: string;
}

export const listInstructorTasks = (courseId: string) =>
  openedx.get<{ tasks: InstructorTask[] }>(`${V2(courseId)}/instructor_tasks`);

/* ---------------------------------------------------------------------- shared ---- */

/** A grade as a whole percentage. The APIs give fractions; every screen shows percentages. */
export const asPercent = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined ? null : Math.round(fraction * 100);

/**
 * How much of the course is behind this learner, 0–1.
 *
 * Locked units stay IN the denominator, deliberately. This is the same arithmetic the
 * learner's own progress page does, and a screen that flatters the number is a screen that
 * disagrees with what the learner is looking at.
 */
export function completionShare(summary: LearnerProgress['completion_summary'] | undefined) {
  if (!summary) return null;
  const total = summary.complete_count + summary.incomplete_count + summary.locked_count;
  return total === 0 ? null : summary.complete_count / total;
}


